import { useLocation, useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { ActionInbox } from "@/components/shared/ActionInbox";
import { MyDayMetricsStrip } from "@/components/shared/MyDayMetricsStrip";
import { EmptyState } from "@/components/shared/EmptyState";
import { Loading } from "@/components/shared/Loading";
import { useActions, useCommercialList } from "@/api/hooks";

// Phase 4.6 — Sales "My Day": the real daily work desk, built entirely on
// GET /actions/sales (already Phase 3 Task-derived — see
// backend/server.py `actions()`), grouped into the categories a Sales
// employee actually thinks in, plus new/untouched leads pulled straight
// from GET /commercial/inquiries. No client-side priority invention — the
// grouping below only re-reads `priority`/`category`/`done` the backend
// already computed.
const SECTIONS = [
  { key: "overdue", title: "Overdue", empty: "Nothing overdue — you're on top of it." },
  { key: "missing_info", title: "Missing information", empty: "No customer information is currently blocking a deal." },
  { key: "follow_up", title: "Follow-ups due", empty: "No customer follow-ups are due right now." },
  { key: "other", title: "Other open work", empty: "Nothing else needs attention." },
];

export default function SalesMyDay() {
  const { data: actions, isLoading } = useActions("sales");
  const { data: list } = useCommercialList();
  const navigate = useNavigate();
  const location = useLocation();
  if (isLoading || !actions) return <Loading />;

  // Sales tasks remain visible, but post-Sales stage profiles (Job creation,
  // Operations and downstream Finance execution) are not relevant on the
  // Sales work desk. Keep stages 1–10 and suppress only later-stage badges.
  const salesActions = actions.map((action) => {
    const stageNumber = Number(action.businessStage?.number);
    if (!Number.isFinite(stageNumber) || stageNumber < 11) return action;
    const { businessStage, ...salesAction } = action;
    return salesAction;
  });
  const open = salesActions.filter((a) => !a.done);
  const completed = salesActions.filter((a) => a.done);
  const urgent = open.filter((a) => a.priority === "P1" || a.priority === "P2").length;
  const newLeads = (list?.leads || []).filter((l) => l.status === "NEW");
  const inquiries = list?.inquiries || [];
  const isAccepted = (inquiry) => inquiry.status === "WON" || inquiry.acceptanceStatus === "VERIFIED";
  const hasSentQuotation = (inquiry) => Boolean(inquiry.quotationSentAt || Number(inquiry.sentVersionsCount) > 0);
  const inQuotation = inquiries.filter((i) => hasSentQuotation(i) && !isAccepted(i) && !["LOST", "EXPIRED", "ON_HOLD_CUSTOMER"].includes(i.status));
  const awaitingCustomer = inQuotation.length;
  const readyForFinance = inquiries.filter(isAccepted).length;
  const followUpsDue = open.filter((a) => (a.category || "") === "follow_up").length;
  const requestedView = new URLSearchParams(location.search || "").get("view") || "all";
  const view = ["open", "urgent", "follow-up", "completed"].includes(requestedView) ? requestedView : "all";
  const visibleActions = view === "urgent"
    ? open.filter((a) => a.priority === "P1" || a.priority === "P2")
    : view === "follow-up"
      ? open.filter((a) => (a.category || "") === "follow_up")
      : view === "completed" ? completed : open;
  const visibleSections = view === "completed"
    ? [{ key: "completed", title: "Completed today", items: completed }]
    : SECTIONS.map((section) => ({ ...section, items: visibleActions.filter((a) => (a.category || "other") === section.key) }));
  const showNewLeadList = view === "all";
  const noVisibleWork = visibleActions.length === 0 && (!showNewLeadList || newLeads.length === 0);

  // Item 5/13 — slim, clickable metrics strip. Every number is real
  // (derived from GET /commercial/inquiries and GET /actions/sales, both
  // already fetched above) and every tile redirects to the filtered list
  // behind it, per the "metrics are never just decoration" standard.
  const metrics = [
    { key: "new-leads", label: "New Leads", value: newLeads.length, onClick: (nav) => nav("/sales/pipeline?view=new-leads"), tone: newLeads.length ? "warn" : "default" },
    { key: "in-quotation", label: "In Quotation", value: inQuotation.length, onClick: (nav) => nav("/sales/pipeline?view=quotation") },
    { key: "follow-ups-due", label: "Follow-ups Due", value: followUpsDue, onClick: (nav) => nav("/sales/inbox?view=follow-up"), tone: followUpsDue ? "warn" : "default" },
    { key: "ready-for-finance", label: "Ready for Finance Review", value: readyForFinance, onClick: (nav) => nav("/sales/pipeline?view=accepted") },
  ];

  return (
    <>
      <PageHeader title="My Day" subtitle={`${open.length} open item${open.length !== 1 ? "s" : ""} · ${urgent} urgent`} />
      <div className="flex-1 overflow-y-auto">
        <MyDayMetricsStrip items={metrics} className="pt-4 sm:grid-cols-2 lg:grid-cols-4" />
        {showNewLeadList && newLeads.length > 0 && (
          <div className="px-4 pt-4 md:px-6">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">New enquiries needing first contact</div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden divide-y divide-slate-100">
              {newLeads.slice(0, 8).map((l) => (
                <button key={l.id} data-testid={`myday-lead-${l.id}`} onClick={() => navigate(`/sales/leads/${l.id}`)}
                  className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div><span className="text-sm font-semibold text-slate-800">{l.company || l.id}</span><span className="text-[12px] text-slate-400 ml-2">via {l.source}</span></div>
                  <span className="text-[11px] text-indigo-600 font-medium">Contact now →</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {view === "all" && awaitingCustomer > 0 && (
          <div className="px-4 pt-4 text-[12px] text-slate-500 md:px-6">
            {awaitingCustomer} quotation{awaitingCustomer !== 1 ? "s" : ""} sent and awaiting a customer response.
          </div>
        )}

        {noVisibleWork ? (
          // Keep the empty state on the same compact spacing rhythm as the
          // other Sales dashboard sections.
          <div className="px-4 pt-4 md:px-6">
            <EmptyState
              title={view === "all" ? "You're caught up" : "No matching items"}
              sub={view === "all" ? "No enquiries, follow-ups or missing information need your attention right now." : "There are currently no records for this KPI."}
            />
          </div>
        ) : (
          visibleSections.map((s) => {
            const items = s.items;
            if (items.length === 0) return null;
            return (
              <div key={s.key} className="pt-4">
                <div className="px-4 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 md:px-6">{s.title} · {items.length}</div>
                <div className="mx-4 rounded-xl border border-slate-200 bg-white overflow-hidden md:mx-6">
                  <ActionInbox items={items} />
                </div>
              </div>
            );
          })
        )}
        <div className="h-4" />
      </div>
    </>
  );
}
